import type { TreeData, TreeNode } from '@/api/tree.api';
import {
  NODE_W,
  SYMBOL_W,
  type FamilyTreeLayoutResult,
  type LayoutFamilyLine,
  type LayoutCoupleSymbol,
  type LayoutPerson,
} from './useFamilyTreeLayout';
import { vorfahren } from './aufdecken';

// ─── Ahnentafel: Vorfahren als Fächer ────────────────────────────────────────
//
// Das allgemeine Layout ordnet nach Familien, Geschwistern und Ehen. Für eine
// reine Ahnenreihe ist das die falsche Frage: Hier gibt es keine Geschwister,
// nur „zwei Eltern je Person, und deren Eltern". Das Bild dafür ist bekannt –
// die Person unten in der Mitte, darüber die Eltern (links Vater, rechts
// Mutter), darüber deren Eltern, jede Generation doppelt so viele. Es wird
// nach hinten breiter wie ein Fächer.
//
// Die Abstände richten sich nach dem, was tatsächlich da ist: Fehlt eine ganze
// Linie, rücken die Nachbarn zusammen, statt eine Lücke für sie zu halten. Ein
// vollständig bekannter Baum ergibt die volle Fächerform; ein lückenhafter
// bleibt kompakt, statt in die Breite zu laufen.

const V_STEP = 230;
const MIN_ELTERN_ABSTAND = SYMBOL_W / 2 + 20 + NODE_W / 2; // wie im allgemeinen Layout
// Zwischen Karten, die nicht Partner sind. Partner brauchen Platz für das
// Paarsymbol (Mitte zu Mitte 240); benachbarte Paare dürfen enger stehen –
// jede Generation verdoppelt die Breite, und jeder Pixel hier zählt oben
// vielfach. Die Paare bleiben dadurch als Paare erkennbar.
const KARTEN_LUECKE = 20;

/** Je Reihe: äußerste Kartenmitten des Teilbaums, relativ zur Mitte seiner Wurzel. */
type Kontur = Map<number, { l: number; r: number }>;

interface Knoten {
  id: string;
  /** Vater/Mutter in der Anordnung (links/rechts); nur wer wirklich einen Platz hat. */
  links: Knoten | null;
  rechts: Knoten | null;
  /** Abstand der Eltern von der Mitte dieser Person (bei zwei Eltern). */
  a: number;
  kontur: Kontur;
}

/** Wer links steht: der Mann, sonst die Hauptelternperson. */
function ordne(p1: TreeNode, p2: TreeNode): [TreeNode, TreeNode] {
  const tauschen = (p1.gender === 'female' && p2.gender !== 'female') ||
    (p2.gender === 'male' && p1.gender !== 'male');
  return tauschen ? [p2, p1] : [p1, p2];
}

function elternVon(n: TreeNode, nachId: Map<string, TreeNode>): string[] {
  return [n.primaryParentId, n.secondaryParentId].filter(
    (e, i, alle): e is string => !!e && nachId.has(e) && alle.indexOf(e) === i,
  );
}

/**
 * Die Reihe jeder Person: der LÄNGSTE Weg von der Wurzel zu ihr.
 *
 * Mit dem kürzesten (oder ersten) Weg stünde bei Ahnenschwund über
 * verschiedene Generationen – der Onkel heiratet die Nichte – eine Person in
 * derselben Reihe wie ihre Eltern, und die Linie liefe waagerecht. So stehen
 * Eltern immer über ihren Kindern. Höchstens so viele Runden wie Personen:
 * ein Kreis in den Daten hält die Rechnung nicht auf.
 */
function reihen(nachId: Map<string, TreeNode>, wurzelId: string): Map<string, number> {
  const tiefe = new Map<string, number>([[wurzelId, 0]]);
  for (let runde = 0; runde < nachId.size; runde++) {
    let geaendert = false;
    for (const [id, t] of tiefe) {
      for (const e of elternVon(nachId.get(id)!, nachId)) {
        if (e === wurzelId) continue;
        if ((tiefe.get(e) ?? -1) < t + 1) {
          tiefe.set(e, t + 1);
          geaendert = true;
        }
      }
    }
    if (!geaendert) break;
  }
  return tiefe;
}

/**
 * Die Vorfahren von `wurzelId` als Ahnentafel anordnen.
 *
 * Gibt dasselbe Ergebnis zurück wie das allgemeine Layout, damit Linien,
 * Paarsymbole und Karten unverändert gezeichnet werden.
 */
export function legeAhnentafel(daten: TreeData, wurzelId: string): FamilyTreeLayoutResult {
  const menge = vorfahren(daten, wurzelId);
  const nachId = new Map<string, TreeNode>();
  for (const n of daten.nodes) if (menge.has(n.id)) nachId.set(n.id, n);
  if (!nachId.has(wurzelId)) return { layoutPersons: [], layoutSymbols: [], layoutLines: [] };

  const tiefe = reihen(nachId, wurzelId);
  const maxTiefe = Math.max(...tiefe.values());

  // Wer über mehrere Wege Vorfahr ist (Heirat unter Verwandten), bekommt nur
  // EINEN Platz – den beim ersten Besuch. Die Linien des anderen Kindes zeigen
  // dann auf diesen Platz.
  const belegt = new Set<string>();

  const baue = (id: string): Knoten => {
    belegt.add(id);
    const n = nachId.get(id)!;
    const eltern = elternVon(n, nachId)
      .filter((e) => !belegt.has(e))
      .map((e) => nachId.get(e)!);

    const k: Knoten = {
      id, links: null, rechts: null, a: 0,
      kontur: new Map([[tiefe.get(id)!, { l: 0, r: 0 }]]),
    };
    const uebernimm = (e: Knoten, dx: number) => {
      for (const [t, { l, r }] of e.kontur) {
        const alt = k.kontur.get(t);
        k.kontur.set(t, alt ? { l: Math.min(alt.l, l + dx), r: Math.max(alt.r, r + dx) } : { l: l + dx, r: r + dx });
      }
    };

    if (eltern.length === 1) {
      k.links = baue(eltern[0].id); // ohne Partner genau darüber
      uebernimm(k.links, 0);
    } else if (eltern.length === 2) {
      const [l, r] = ordne(eltern[0], eltern[1]);
      const kl = baue(l.id);
      // Der zweite Elternteil könnte beim Aufbau des ersten schon vergeben worden sein.
      const kr = belegt.has(r.id) ? null : baue(r.id);
      k.links = kl;
      if (kr) {
        k.rechts = kr;
        // Reihe für Reihe: nur wo beide Seiten Karten haben, müssen sie sich
        // ausweichen. Eine fehlende Linie lässt so wirklich Platz frei, statt
        // dass die ganze Breite des Nachbarn freigehalten wird.
        let a = MIN_ELTERN_ABSTAND;
        for (const [t, links] of kl.kontur) {
          const rechts = kr.kontur.get(t);
          if (rechts) a = Math.max(a, (links.r - rechts.l + NODE_W + KARTEN_LUECKE) / 2);
        }
        k.a = a;
        uebernimm(kl, -a);
        uebernimm(kr, a);
      } else {
        uebernimm(kl, 0);
      }
    }
    return k;
  };

  const wurzel = baue(wurzelId);

  const pos = new Map<string, { x: number; y: number }>();
  const platziere = (k: Knoten, x: number) => {
    pos.set(k.id, { x, y: (maxTiefe - tiefe.get(k.id)!) * V_STEP });
    if (k.links && k.rechts) {
      platziere(k.links, x - k.a);
      platziere(k.rechts, x + k.a);
    } else if (k.links) {
      platziere(k.links, x);
    }
  };
  platziere(wurzel, 0);

  // Wie das allgemeine Layout: linke obere Kartenmitte bei (40, 40).
  let minX = Infinity;
  for (const p of pos.values()) minX = Math.min(minX, p.x);
  const dx = 40 - minX;
  const dy = 40;
  for (const p of pos.values()) {
    p.x += dx;
    p.y += dy;
  }

  // Linien und Paarsymbole: je Elternpaar (oder Einzelelternteil) eine Linie.
  const symbole: LayoutCoupleSymbol[] = [];
  const linien: LayoutFamilyLine[] = [];
  const gruppen = new Map<string, { eltern: string[]; kinder: string[] }>();
  for (const n of nachId.values()) {
    const eltern = [n.primaryParentId, n.secondaryParentId].filter(
      (e, i, alle): e is string => !!e && pos.has(e) && alle.indexOf(e) === i,
    );
    if (eltern.length === 0 || !pos.has(n.id)) continue;
    const key = [...eltern].sort().join(':');
    if (!gruppen.has(key)) gruppen.set(key, { eltern, kinder: [] });
    gruppen.get(key)!.kinder.push(n.id);
  }

  let farbe = 0;
  for (const { eltern, kinder } of gruppen.values()) {
    const p1 = pos.get(eltern[0])!;
    const p2 = eltern[1] ? pos.get(eltern[1])! : null;
    const symbolX = p2 ? (p1.x + p2.x) / 2 : p1.x;
    if (p2) {
      const link =
        daten.links.find(
          (l) =>
            (l.person1Id === eltern[0] && l.person2Id === eltern[1]) ||
            (l.person1Id === eltern[1] && l.person2Id === eltern[0]),
        ) ?? null;
      symbole.push({ x: symbolX, y: p1.y, link });
    }
    const kinderPos = kinder.map((id) => pos.get(id)!);
    linien.push({
      symbolX,
      parentY: p1.y,
      junctionY: (p1.y + Math.min(...kinderPos.map((c) => c.y))) / 2,
      isCouple: !!p2,
      childPositions: kinderPos.map((c) => ({ x: c.x, y: c.y })),
      colorIndex: farbe++,
    });
  }

  const personen: LayoutPerson[] = daten.nodes
    .filter((n) => pos.has(n.id))
    .map((n) => ({ x: pos.get(n.id)!.x, y: pos.get(n.id)!.y, data: n }));

  return { layoutPersons: personen, layoutSymbols: symbole, layoutLines: linien };
}

/** Besteht die aufgedeckte Menge genau aus der Ahnenreihe dieser Person? */
export function istReineAhnenreihe(
  daten: TreeData,
  aufgedeckt: ReadonlySet<string>,
  wurzelId: string,
): boolean {
  const v = vorfahren(daten, wurzelId);
  if (v.size !== aufgedeckt.size) return false;
  for (const id of v) if (!aufgedeckt.has(id)) return false;
  return true;
}
